import { paletteVar } from "@platform/design-system";

import type { ColumnWidthPreset } from "@platform/btx-plan-designer";

export const QUOTES_GRID_ID = "rfp-quotes";

export const GRID_CONTAINER_SX = { display: "flex", flexDirection: "column" };

export const COLUMN_WIDTH_SCALE: Record<ColumnWidthPreset, number> = {
  narrow: 0.35,
  medium: 0.65,
  wide: 1,
};

export const QUOTES_PLAN_EDITOR_SX = {
  height: "auto",
  "& .ag-header-row .ag-header-cell, & .ag-header-row .ag-header-group-cell": {
    borderColor: "divider",
  },
  "& .ag-header-row .benefits-header": {
    backgroundColor: paletteVar("background-paper", "#ffffff"),
    color: paletteVar("text-primary", "#00344f"),
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  "& .header-carrier-group": {
    backgroundColor: paletteVar("background-paper", "#ffffff"),
    color: paletteVar("text-primary", "#00344f"),
    fontSize: "0.875rem",
    fontWeight: 500,
    letterSpacing: 0,
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  "& .header-carrier-group .ag-header-group-text": {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  // NOTE: never set `position: relative` on AG Grid header cells — the grid
  // positions them absolutely, and re-positioning drops them into normal flow
  // where adjacent carrier cells stack one row-height below each other (the
  // carrier-name staircase). The ::before divider anchors fine to the
  // absolutely-positioned cell itself.
  "& .ag-header-group-cell.header-carrier-group + .ag-header-group-cell.header-carrier-group::before":
    {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      width: "1px",
      backgroundColor: "divider",
      pointerEvents: "none",
    },
  "& .header-level-0 .ag-header-group-text, & .header-level-0 .ag-header-cell-text":
    {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 24,
      padding: "2px 10px",
      borderRadius: 1,
      backgroundColor: paletteVar("surfaces-neutral", "#eeeeee"),
      color: paletteVar("text-secondary", "#777777"),
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.4,
      maxWidth: "100%",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      userSelect: "text",
      cursor: "text",
    },
  "& .header-level-2": {
    backgroundColor: paletteVar("surfaces-neutral", "#eef3f5"),
    color: paletteVar("text-secondary", "#555555"),
    fontWeight: 400,
  },
};

type ZIndexTheme = { zIndex: { appBar: number } };

export const fullScreenOverlayZIndex = (theme: ZIndexTheme): number =>
  theme.zIndex.appBar + 1;

// AG Grid renders its popups, the Quotes popup cell editors among them, into
// document.body under a low z-index, so the full-screen overlay would paint over
// them. One layer above the overlay is enough, and it has to stay below
// theme.zIndex.modal: the MUI menus these editors open (the Type dropdown) render
// at that layer, so a popup at or above it hides its own dropdown.
export const getFullScreenPopupStyles = (
  theme: ZIndexTheme,
): { ".ag-popup, .ag-popup-child": { zIndex: number } } => ({
  ".ag-popup, .ag-popup-child": { zIndex: fullScreenOverlayZIndex(theme) + 1 },
});

// In full screen the SBS view becomes a fixed overlay above the app header, so
// the toolbar (the only exit control) must live inside the overlay and stay
// pinned while the grid scrolls — otherwise there is no way back out.
export const getFullScreenOverlaySx = (
  isFullScreen: boolean,
): Record<string, unknown> =>
  isFullScreen
    ? {
        position: "fixed",
        inset: 0,
        zIndex: fullScreenOverlayZIndex,
        overflow: "auto",
        bgcolor: "background.default",
        // No top padding: the sticky toolbar pins at top:0, so any padding here
        // would leave a background strip above the pinned bar while scrolling.
        px: 2,
        pb: 2,
      }
    : {};

export const getStickyToolbarSx = (
  isFullScreen: boolean,
): Record<string, unknown> | undefined =>
  isFullScreen
    ? {
        position: "sticky",
        top: 0,
        zIndex: (theme: { zIndex: { appBar: number } }) =>
          theme.zIndex.appBar + 2,
        bgcolor: "background.default",
      }
    : undefined;

// PrimaryBar's Toolbar is height: "4rem" (design-system primary-bar.tsx), and
// NewAnalystRfpDetails.tsx already offsets the page by the same amount
// (mt={8} = 4rem) to clear the fixed AppBar it renders in. Kept as a CSS
// length rather than a px constant so the offset tracks the root font size
// (e.g. accessibility text scaling) instead of drifting from it.
export const APP_BAR_HEIGHT = "4rem";

/**
 * `top` offset for the grid's sticky header. In full screen the toolbar owns
 * the top strip and is measured live (see `useElementHeight`) rather than
 * hardcoded, since it isn't wrapped and its height is otherwise correct only
 * by coincidence. In normal page scroll the grid pins beneath the fixed
 * AppBar instead.
 */
export const getStickyHeaderTop = (
  isFullScreen: boolean,
  toolbarHeight: number,
): number | string => (isFullScreen ? toolbarHeight : APP_BAR_HEIGHT);
